import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { useQuery } from '../../../../hooks';
import { LANDING_ROUTE } from '../../../navigation/routes.constants';
import {
  CERTIFICATE_LINKING_STEPS,
} from '../../petDemographics.constants';
import {
  resetNoMatchError,
  updateCustomerEmail as updateCustomerEmailAction,
  validateEmail as validateEmailAction,
  verifyCustomer as verifyCustomerAction,
} from '../../../../actions';
import { EMAIL_OPTIONS } from '../utils';

const useCertificateLinkingScreen = () => {
  const history = useHistory();
  const query = useQuery();
  const groupNumber = query.get('groupNumber') || '';
  const emailFromUrl = query.get('email') || '';

  const store = useSelector(({ petDemographics }) => ({
    petDemographics,
  }), shallowEqual);
  const {
    petDemographics: {
      customerVerification,
      emailValidation,
      updateCustomerEmail: updateCustomerEmailState,
    },
  } = store;
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(
    CERTIFICATE_LINKING_STEPS.LANDING,
  );
  const [email, setEmail] = useState(emailFromUrl);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [stepHistory, setStepHistory] = useState([
    CERTIFICATE_LINKING_STEPS.LANDING,
  ]);
  // Tracks our own updateCustomerEmail dispatch specifically — that redux
  // slice is shared with useAuthorizationCode's OTP flow, which also
  // listens to updateCustomerEmail loading/updated and transitions to
  // CERTIFICATE_LINKING_STEPS.EMAIL_UPDATED_SUCCESS.
  const [isLinkingEmailDirectly, setIsLinkingEmailDirectly] = useState(false);

  const onSignInClick = useCallback(() => {
    history.push(LANDING_ROUTE);
  }, [history]);

  const handleStepChange = useCallback((step) => {
    setCurrentStep(step);
    setStepHistory((prevState) => [...prevState, step]);
  }, []);

  const handleGoBack = useCallback(() => {
    if (stepHistory.length > 1) {
      if (currentStep === CERTIFICATE_LINKING_STEPS.VERIFY_DETAILS) {
        dispatch(resetNoMatchError());
      }
      const newHistory = stepHistory.slice(0, -1);
      setStepHistory(newHistory);
      setCurrentStep(newHistory[newHistory.length - 1]);
    }
  }, [currentStep, dispatch, stepHistory]);

  useEffect(() => {
    if (!emailValidation.validated) {
      return;
    }

    if (!emailValidation.emailExists) {
      // non existing email, so verify details (no skippable)
      handleStepChange(CERTIFICATE_LINKING_STEPS.VERIFY_DETAILS);
      return;
    }

    if (!emailValidation.hasGroupPolicy) {
      // existing email, but no group policy (skippable)
      handleStepChange(CERTIFICATE_LINKING_STEPS.VERIFY_DETAILS);
      return;
    }

    // here starts the tricky stuff...
    if (emailValidation.hasB2CCredentials) {
      // existing email with group policy
      handleStepChange(CERTIFICATE_LINKING_STEPS.ALREADY_REGISTERED);
    } else {
      // TODO: sign up process on B2C... :skull:
      // eslint-disable-next-line no-console
      console.warn('TODO: trigger B2C sign up process...');
    }
  }, [emailValidation]);

  const validateEmail = useCallback((emailToValidate) => {
    setEmail(emailToValidate);
    dispatch(validateEmailAction({ email: emailToValidate }));
  }, [dispatch]);

  const handleVerifyCustomer = useCallback(({
    dateOfHire, firstName, lastName, zipCode,
  }) => {
    if (!groupNumber) {
      return;
    }

    const yearMonthOfHire = dateOfHire
      ? moment(dateOfHire).format('YYYY-MM')
      : undefined;

    dispatch(verifyCustomerAction({
      email, firstName, groupNumber, lastName, yearMonthOfHire, zipCode,
    }));
  }, [dispatch, email, groupNumber]);

  useEffect(() => {
    if (!customerVerification.verified) {
      return;
    }

    const { certificates } = customerVerification;

    if (certificates.length === 0) {
      // NO MATCH — stay on VERIFY_DETAILS, showNoMatchError handles UI
      return;
    }

    if (certificates.length === 1) {
      const [certificate] = certificates;

      if (!certificate.emailMatched && certificate.email) {
        setRegisteredEmail(certificate.email);
        setCertificateNumber(certificate.certificateNumber);
        handleStepChange(CERTIFICATE_LINKING_STEPS.SELECT_EMAIL);
      } else if (!certificate.emailMatched && !certificate.email) {
        setCertificateNumber(certificate.certificateNumber);
        setIsLinkingEmailDirectly(true);
        dispatch(updateCustomerEmailAction({
          certificateNumber: certificate.certificateNumber,
          email,
        }));
      } else {
        handleStepChange(CERTIFICATE_LINKING_STEPS.EMAIL_LINKED_SUCCESS);
      }

      return;
    }

    // NON UNIQUE MATCH — let user pick from dropdown
    handleStepChange(CERTIFICATE_LINKING_STEPS.VERIFY_DATE_OF_HIRE);
  }, [customerVerification.verified, customerVerification.certificates]);

  useEffect(() => {
    if (!isLinkingEmailDirectly) {
      return;
    }

    if (updateCustomerEmailState.loading) {
      return;
    }

    if (updateCustomerEmailState.updated) {
      setIsLinkingEmailDirectly(false);
      handleStepChange(CERTIFICATE_LINKING_STEPS.EMAIL_LINKED_SUCCESS);
    } else if (updateCustomerEmailState.error) {
      // let the user retry from VERIFY_DETAILS
      setIsLinkingEmailDirectly(false);
    }
  }, [
    handleStepChange,
    isLinkingEmailDirectly,
    updateCustomerEmailState.error,
    updateCustomerEmailState.loading,
    updateCustomerEmailState.updated,
  ]);

  const showNoMatchError = useMemo(() => (
    (customerVerification.verified
      && customerVerification.certificates.length === 0)
    || !!customerVerification.error
  ), [
    customerVerification.certificates,
    customerVerification.error,
    customerVerification.verified,
  ]);

  const showSkipButton = useMemo(() => (
    emailValidation.emailExists
    && !emailValidation.hasGroupPolicy
  ), [
    emailValidation.emailExists,
    emailValidation.hasGroupPolicy,
  ]);

  const handleSelectEmailContinue = useCallback((selectedOption) => {
    if (selectedOption === EMAIL_OPTIONS.NEW_EMAIL) {
      handleStepChange(CERTIFICATE_LINKING_STEPS.CONFIRM_REGISTERED_EMAIL);
    } else {
      onSignInClick();
    }
  }, [handleStepChange, onSignInClick]);

  const handleConfirmRegisteredEmail = useCallback(() => {
    handleStepChange(CERTIFICATE_LINKING_STEPS.ENTER_AUTHORIZATION_CODE);
  }, [handleStepChange]);

  return {
    certificateNumber,
    certificates: customerVerification.certificates,
    currentStep,
    email,
    handleConfirmRegisteredEmail,
    handleGoBack,
    handleSelectEmailContinue,
    handleVerifyCustomer,
    onSignInClick,
    registeredEmail,
    setCertificateNumber,
    setCurrentStep: handleStepChange,
    setEmail,
    setRegisteredEmail,
    showNoMatchError,
    showSkipButton,
    validateEmail,
    validatingEmail: emailValidation.loading,
    verifyingCustomer: customerVerification.loading
      || (isLinkingEmailDirectly && updateCustomerEmailState.loading),
  };
};

export { useCertificateLinkingScreen };
